import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import type {DirectTurnSurface} from '@github-ui/ahp-session-client/contracts'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useRef} from 'react'

export const SESSION_RESUME_ERROR_EVENT = 'copilot_chat.session_resume_error'

/**
 * The launch path that owns the session, using the same `entrypoint` values as the AHP session
 * lifecycle events (`copilot_chat.ahp_create_session` and `copilot_chat.ahp_session_not_found`).
 */
export type SessionResumeErrorEntrypoint = DirectTurnSurface | 'contextual_chat' | 'unknown'

/** Identifies the session a resume error belongs to, so it can be joined with lifecycle events. */
export interface SessionResumeErrorContext {
  entrypoint: SessionResumeErrorEntrypoint | undefined
  taskId?: string
  sessionId?: string
  environmentId?: string
}

type RpcFailureSubtype = NonNullable<Extract<RelayFailure, {kind: 'rpc'}>['subtype']>

const RPC_SUBTYPE_REASONS: Record<RpcFailureSubtype, string> = {
  authenticateUnavailable: 'rpc_authenticate_unavailable',
  cliVersionMismatch: 'rpc_cli_version_mismatch',
  sessionNotFound: 'rpc_session_not_found',
}

const MAX_SERVER_CODE_LENGTH = 40

// Server codes are API-controlled, so normalize them to keep the reason short and low-cardinality.
function tagSafeServerCode(code: string): string {
  const normalized = code
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+/, '')
    .slice(0, MAX_SERVER_CODE_LENGTH)
  return normalized || 'other'
}

/**
 * Maps a relay failure to the bounded `reason` field of the session resume error event.
 * An error shown without structured failure details is `unclassified`.
 */
export function getSessionResumeErrorReason(failure: RelayFailure | undefined): string {
  if (!failure) return 'unclassified'
  switch (failure.kind) {
    case 'cancelled':
      return 'cancelled'
    case 'transport':
      return `transport_${failure.reason.replaceAll('-', '_')}`
    case 'http':
      return failure.code ? `http_${tagSafeServerCode(failure.code)}` : `http_${failure.status}`
    case 'rpc':
      return failure.subtype ? RPC_SUBTYPE_REASONS[failure.subtype] : `rpc_${failure.code}`
    case 'protocol-version':
      return 'protocol_version'
    case 'wake-timeout':
      return 'wake_timeout'
    case 'resumption-timeout':
      return 'resumption_timeout'
    case 'provisioning':
      return `provisioning_${failure.reason.replaceAll('-', '_')}`
    case 'unknown':
      return 'unknown'
  }
}

export function useSessionResumeErrorTelemetry(): (context: SessionResumeErrorContext, reason: string) => void {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const sendEventRef = useRef(sendAnalyticsEvent)
  useEffect(() => {
    sendEventRef.current = sendAnalyticsEvent
  }, [sendAnalyticsEvent])
  return useCallback(({entrypoint, taskId, sessionId, environmentId}: SessionResumeErrorContext, reason: string) => {
    sendEventRef.current(SESSION_RESUME_ERROR_EVENT, undefined, {
      entrypoint: entrypoint ?? 'unknown',
      reason,
      ...(taskId ? {task_id: taskId} : {}),
      ...(sessionId ? {session_id: sessionId} : {}),
      ...(environmentId ? {environment_id: environmentId} : {}),
    })
  }, [])
}
